/** Inline advisor notes preview for the agent review Artefacts step. */

import { useEffect, useState } from "react";

import { fetchArtifactPreviewText } from "@/services/api/agentic-v2";
import type { ArtefactReviewItem } from "./types";

interface AdvisorNotesArtefactPreviewProps {
  artefact: ArtefactReviewItem;
  className?: string;
}

export function AdvisorNotesArtefactPreview({ artefact, className }: AdvisorNotesArtefactPreviewProps) {
  const [text, setText] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setNotice(null);
    setError(null);

    void fetchArtifactPreviewText(artefact.artifact_id)
      .then((result) => {
        if (cancelled) return;
        setText(result.text);
        setNotice(result.notice ?? null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load advisor notes preview.");
      });

    return () => {
      cancelled = true;
    };
  }, [artefact.artifact_id]);

  if (error) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        {error}
      </div>
    );
  }

  if (text === null) {
    return (
      <div className="rounded-xl bg-white/[0.02] px-4 py-6 text-sm text-slate-400 ring-1 ring-white/5">
        Loading advisor notes…
      </div>
    );
  }

  return (
    <div
      className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white text-slate-900 ring-1 ring-white/10 ${className ?? ""}`}
    >
      {notice ? (
        <p className="flex-shrink-0 border-b border-slate-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {notice}
        </p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-800">
          {text || "No text could be extracted from this document."}
        </pre>
      </div>
    </div>
  );
}
